/**
 * @fileoverview
 *
 * These are common utilities for operations to/from/on Temporal objects.
 * They are used in the implementation of our isoDateString and isoDateFields
 * utilties but are not intended to be exported for broader use.
 *
 */

import { Temporal, toTemporalInstant } from '@js-temporal/polyfill';

import type { IIsoDateFields } from './types';

type PlainDate = Temporal.PlainDate;
type ZonedDateTime = Temporal.ZonedDateTime;

const getPlainDateFrom = Temporal.PlainDate.from;

export const getCurrentPlainDate = (): PlainDate => {
  return Temporal.Now.plainDateISO();
};

export const getPlainDateOrNullFrom = (item: string | IIsoDateFields): PlainDate | null => {
  try {
    return getPlainDateFrom(item, { overflow: 'reject' });
  } catch {
    return null;
  }
};

export const getPlainDateOrNullFromDate = (date: Date): PlainDate | null => {
  try {
    return toTemporalInstant
      .apply(date)
      .toZonedDateTimeISO(Temporal.Now.timeZoneId())
      .toPlainDate();
  } catch {
    return null;
  }
};

export const getZonedDateTimeOrNullFromIsoDateTimeString = (
  dateTimeString: string
): ZonedDateTime | null => {
  try {
    return Temporal.ZonedDateTime.from(`${dateTimeString}[${Temporal.Now.timeZoneId()}]`, {
      overflow: 'reject',
      offset: 'use',
    });
  } catch {
    return null;
  }
};

export const getZonedDateTimeOrNullFromIsoDateString = (
  dateString: string,
  options?: {
    timeZone?: string;
    plainTime?: string;
  }
): ZonedDateTime | null => {
  try {
    return getPlainDateFrom(dateString).toZonedDateTime({
      timeZone: options?.timeZone ?? Temporal.Now.timeZoneId(),
      plainTime: options?.plainTime,
    });
  } catch {
    return null;
  }
};

export const isPlainDateAfter = (date: PlainDate, dateToCompare: PlainDate): boolean => {
  return Temporal.PlainDate.compare(date, dateToCompare) > 0;
};

export const isPlainDateBefore = (date: PlainDate, dateToCompare: PlainDate): boolean => {
  return Temporal.PlainDate.compare(date, dateToCompare) < 0;
};
